import { useEffect } from "react";
import {
  Box,
  Theme,
  makeStyles,
  createStyles,
  Typography,
  ListItem as MuiListItem,
  List,
  ListItemIcon,
  Collapse,
  SvgIcon,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import clsx from "clsx";

import { LinksType } from "~components/Menu";
import { Link } from "~components/Link";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
      marginBottom: theme.spacing(2),

      fontSize: theme.spacing(2),
      lineHeight: "21px",
      letterSpacing: 0,
      color: theme.palette.blueGrey.main,

      cursor: "pointer",

      transition: "all ease-in-out .2s",

      "&:hover, &:active": {
        backgroundColor: "rgba(245, 248, 254, 0.08)",
        boxShadow: `inset 2px 0 0 ${theme.palette.primary.main}`,
        color: "white",
        textDecoration: "none",
      },

      "& svg": {
        fontSize: theme.spacing(2.5),
      },

      "& span": {
        marginLeft: theme.spacing(1.75),
      },
    },
    active: {
      color: "white",

      backgroundColor: "rgba(245, 248, 254, 0.08)",
      boxShadow: `inset 2px 0 0 ${theme.palette.primary.main}`,
    },
    icon: {
      marginRight: theme.spacing(1.25),

      minWidth: theme.spacing(3),
      height: theme.spacing(3),

      display: "flex",
      justifyContent: "center",
      alignItems: "center",

      color: "inherit",
    },
    label: {
      fontSize: 16,
      lineHeight: "21px",
      fontWeight: "normal",
    },
  }),
);

type ListItemProps = {
  activePath: string;
  isOpen: string | undefined;
  setIsOpen: (v: string | undefined) => void;
} & LinksType[number];

const isActive = (path: string, activePath: string) => {
  if (path === "/") {
    return path === activePath;
  }

  return activePath.startsWith(path);
};

export const ListItem = ({
  i18nName,
  path,
  showChildrenInSidebar,
  children,
  activePath,
  isOpen,
  setIsOpen,
  ...props
}: ListItemProps) => {
  const classes = useStyles();

  useEffect(() => {
    if (isActive(path, activePath)) {
      setIsOpen(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath]);

  return (
    <>
      <MuiListItem
        className={clsx(classes.listItem, {
          [classes.active]: !showChildrenInSidebar && isActive(path, activePath),
        })}
        {...((!showChildrenInSidebar && { to: path, component: Link }) || {
          onClick: () => setIsOpen(isOpen === path ? undefined : path),
        })}
      >
        <ListItemIcon className={classes.icon}>
          {props?.icon ? <SvgIcon component={props.icon} /> : null}
        </ListItemIcon>
        {showChildrenInSidebar ? (
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography className={classes.label}>{i18nName}</Typography>
            <ExpandMore
              style={{
                transform: `rotate(${isOpen === path ? -180 : 0}deg)`,
                transition: "transform ease-in-out .2s",
              }}
            />
          </Box>
        ) : (
          <Typography className={classes.label}>{i18nName}</Typography>
        )}
      </MuiListItem>
      {showChildrenInSidebar && (
        <Collapse in={isOpen === path} timeout="auto" unmountOnExit>
          <List style={{ padding: 0, paddingLeft: 24 }}>
            {children?.map((link) => (
              <MuiListItem
                to={link.path}
                component={Link}
                key={`list-item-${link.path}`}
                className={clsx(classes.listItem, {
                  [classes.active]: isActive(link.path, activePath),
                })}
              >
                <ListItemIcon className={classes.icon}>
                  {typeof link?.icon !== "undefined" ? <SvgIcon component={link.icon} /> : null}
                </ListItemIcon>
                <Typography className={classes.label}>{link.i18nName}</Typography>
              </MuiListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};
